/**
 * Documents API — Connect RPC wrapper for shirohige (DocumentsService).
 *
 * Owns the collaborative document editor flow:
 *   - list/create/rename/move/delete documents
 *   - folders
 *   - getEditSession (returns signed ONLYOFFICE editor config)
 *   - list/restore versions
 *
 * The binary transport is used for CreateDocument so we don't pay base64
 * overhead on .docx/.xlsx/.pptx uploads.
 */ import { Struct } from '@bufbuild/protobuf';
import { documentsBinaryClient, documentsClient } from '../connect/clients';
// ---- Proto enum mapping ----
const DOC_TYPE_TO_PROTO = {
    docx: 1,
    xlsx: 2,
    pptx: 3
};
function docTypeFromProto(t) {
    switch(t){
        case 2:
            return 'xlsx';
        case 3:
            return 'pptx';
        default:
            return 'docx';
    }
}
function timestampToString(ts) {
    if (!ts) return '';
    return new Date(Number(ts.seconds) * 1000).toISOString();
}
function mapDocument(d) {
    return {
        id: d.id,
        tenantId: d.tenantId,
        ownerUserId: d.ownerUserId,
        folderId: d.folderId || undefined,
        name: d.name,
        documentType: docTypeFromProto(d.documentType),
        sizeBytes: Number(d.sizeBytes),
        versionCount: d.versionCount,
        createdWithContent: d.createdWithContent,
        createdAt: timestampToString(d.createdAt),
        updatedAt: timestampToString(d.updatedAt)
    };
}
function mapFolder(f) {
    return {
        id: f.id,
        tenantId: f.tenantId,
        ownerUserId: f.ownerUserId,
        parentId: f.parentId || undefined,
        name: f.name,
        createdAt: timestampToString(f.createdAt),
        updatedAt: timestampToString(f.updatedAt)
    };
}
function mapVersion(v) {
    return {
        id: v.id,
        documentId: v.documentId,
        versionNumber: v.versionNumber,
        sizeBytes: Number(v.sizeBytes),
        createdByUserId: v.createdByUserId,
        createdAt: timestampToString(v.createdAt),
        restoredFromVersionId: v.restoredFromVersionId || undefined
    };
}
export const documentsApi = {
    async listDocuments (params = {}) {
        const resp = await documentsClient.listDocuments({
            folderId: params.folderId ?? '',
            page: params.page ?? 1,
            pageSize: params.pageSize ?? 50,
            search: params.search ?? '',
            allFolders: params.allFolders ?? false
        });
        return {
            documents: resp.documents.map(mapDocument),
            total: resp.total
        };
    },
    async getDocument (id) {
        const resp = await documentsClient.getDocument({
            id
        });
        if (!resp.document) throw new Error('Document not found');
        return mapDocument(resp.document);
    },
    async createDocument (params) {
        const resp = await documentsBinaryClient.createDocument({
            name: params.name,
            documentType: DOC_TYPE_TO_PROTO[params.documentType],
            folderId: params.folderId ?? '',
            initialBytes: params.initialBytes,
            operationId: params.operationId ?? ''
        });
        if (!resp.document) throw new Error('CreateDocument returned no document');
        return mapDocument(resp.document);
    },
    async renameDocument (id, name) {
        const resp = await documentsClient.renameDocument({
            id,
            name
        });
        if (!resp.document) throw new Error('Document not found');
        return mapDocument(resp.document);
    },
    async moveDocument (id, folderId) {
        const resp = await documentsClient.moveDocument({
            id,
            folderId: folderId ?? ''
        });
        if (!resp.document) throw new Error('Document not found');
        return mapDocument(resp.document);
    },
    async deleteDocument (id) {
        await documentsClient.deleteDocument({
            id
        });
    },
    async listFolders (parentId) {
        const resp = await documentsClient.listFolders({
            parentId: parentId ?? ''
        });
        return resp.folders.map(mapFolder);
    },
    async getFolder (id) {
        const resp = await documentsClient.getFolder({
            id
        });
        if (!resp.folder) throw new Error('Folder not found');
        return mapFolder(resp.folder);
    },
    async createFolder (name, parentId) {
        const resp = await documentsClient.createFolder({
            name,
            parentId: parentId ?? ''
        });
        if (!resp.folder) throw new Error('CreateFolder returned no folder');
        return mapFolder(resp.folder);
    },
    async renameFolder (id, name) {
        const resp = await documentsClient.renameFolder({
            id,
            name
        });
        if (!resp.folder) throw new Error('Folder not found');
        return mapFolder(resp.folder);
    },
    async deleteFolder (id) {
        await documentsClient.deleteFolder({
            id
        });
    },
    // WRT-05: actionLinkBookmark opens the editor at that heading bookmark (shirohige adds
    // editorConfig.actionLink before signing).
    async getEditSession (documentId, actionLinkBookmark = '') {
        const resp = await documentsClient.getEditSession({
            documentId,
            actionLinkBookmark
        });
        // Keep the local editor on the checkout's forwarded origin; host :7040
        // may belong to another checkout. Server-side fetch/callback URLs stay signed.
        const localDocumentServer = process.env.NODE_ENV === 'development' && /^http:\/\/(?:localhost|127\.0\.0\.1):7040\/?$/.test(resp.documentServerUrl);
        return {
            editorConfigJson: resp.editorConfigJson,
            documentServerUrl: localDocumentServer ? `${window.location.origin}/onlyoffice` : resp.documentServerUrl,
            documentType: resp.documentType
        };
    },
    async listVersions (documentId) {
        const resp = await documentsClient.listVersions({
            documentId
        });
        // currentVersionId is authoritative for "which is live" — a branched stale
        // save can have a higher versionNumber without being current.
        return {
            versions: resp.versions.map(mapVersion),
            currentVersionId: resp.currentVersionId
        };
    },
    async restoreVersion (documentId, versionId, baseVersionId) {
        const resp = await documentsClient.restoreVersion({
            documentId,
            versionId,
            baseVersionId
        });
        if (!resp.document) throw new Error('RestoreVersion returned no document');
        return mapDocument(resp.document);
    },
    /**
   * Apply AI-proposed edit operations to a document server-side (ONLYOFFICE
   * Document Builder), producing a new version. Returns the new document plus
   * its new ONLYOFFICE cache key so the editor can reload the fresh bytes.
   */ async applyDocumentOperations (documentId, operations) {
        const resp = await documentsClient.applyDocumentOperations({
            documentId,
            operations: operations.map((op)=>({
                    action: op.action,
                    params: Struct.fromJson(op.params ?? {})
                }))
        });
        if (!resp.document) throw new Error('ApplyDocumentOperations returned no document');
        return {
            document: mapDocument(resp.document),
            documentKey: resp.documentKey
        };
    }
};
